import { Control, useWatch } from 'react-hook-form';
import { cn } from '../../../../lib/utils/cn';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../ui/components/ui/form';
import { Input } from '../../../../ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/components/ui/select';
import CheckStatusIcon from '../../../offer-checkout/components/check-status-icon/check-status-icon';
import { countries } from '../../../organisations/root/forms/items/personal-form-items';
import { OFFER_CONFLICTS } from '../../constants/offer-conflicts';
import { NO_SELECTION } from '../../constants/offers-page.constants';
import { AvailabilityCheck, AvailabilityCheckFn, useAvailabilityCheck } from '../../hooks/use-availability-check';
import { toCheckableCompanyName, toCheckableMail } from '../../lib/customer';
import OfferFormSection, { CollapsiblePanelProps } from '../offer-form-section/offer-form-section';
import { ErrorEntity } from '../../../root/context';
import { OfferCustomerFieldName, OfferFormValues } from '../offer-form/offer-form.schema';

export interface OfferCustomerSectionProps {
  control: Control<OfferFormValues>;
  panel: CollapsiblePanelProps;
  /** Live checks while typing – without them (e.g. stories) the fields show no check icon. */
  checkMailAvailability?: AvailabilityCheckFn;
  checkCompanyNameAvailability?: AvailabilityCheckFn;
  /** The contract data of an accepted offer: everything required, the mail (the customer's login) locked. */
  isCorrection?: boolean;
}

/** Prefill for the checkout – everything but the mail stays editable for the customer. */
const OfferCustomerSection = ({
  control,
  panel,
  checkMailAvailability,
  checkCompanyNameAvailability,
  isCorrection = false
}: OfferCustomerSectionProps) => {
  const required = isCorrection ? ' *' : '';

  return (
    <OfferFormSection
      panel={panel}
      title="Kunde"
      description={
        isCorrection
          ? 'Die Vertragsdaten des Kunden. Sie überschreiben auch das Unternehmen und den Admin-Nutzer.'
          : 'Nur die E-Mail ist Pflicht. Alles andere ist eine Vorbefüllung, die der Kunde beim Annehmen ergänzt oder ändert.'
      }>
      {isCorrection ? (
        <CustomerTextField control={control} name="mail" label="E-Mail" type="email" hint="Login des Kunden – nicht änderbar." disabled />
      ) : (
        <CheckedCustomerTextField
          control={control}
          name="mail"
          label="E-Mail *"
          type="email"
          placeholder="z. B. name@firma.de"
          hint="Nicht änderbar für den Kunden."
          toCheckable={toCheckableMail}
          check={checkMailAvailability}
          takenMessage={OFFER_CONFLICTS[ErrorEntity.OfferMailAlreadyInUseConflict]?.message}
        />
      )}

      <div className="grid grid-cols-2 gap-4">
        <CustomerTextField control={control} name="firstName" label={`Vorname${required}`} />
        <CustomerTextField control={control} name="lastName" label={`Nachname${required}`} />
        <CheckedCustomerTextField
          control={control}
          name="name"
          label={`Firma${required}`}
          className="col-span-2"
          toCheckable={toCheckableCompanyName}
          check={checkCompanyNameAvailability}
          takenMessage={OFFER_CONFLICTS[ErrorEntity.OfferCompanyNameAlreadyInUseConflict]?.message}
        />
        <CustomerTextField control={control} name="vatId" label={`USt-ID${required}`} />
        <CustomerTextField control={control} name="phone" label={`Telefon${required}`} type="tel" placeholder="z. B. +49 171 1234567" />
      </div>

      <div className="grid grid-cols-4 gap-4">
        <CustomerTextField control={control} name="street" label={`Straße${required}`} className="col-span-3" />
        <CustomerTextField control={control} name="houseNumber" label={`Hausnr.${required}`} />
        <CustomerTextField control={control} name="postCode" label={`PLZ${required}`} />
        <CustomerTextField control={control} name="city" label={`Ort${required}`} className="col-span-3" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name="customer.country"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-[10px]">
              <FormLabel className="text-[14px] font-bold text-[#12282A]">{`Land${required}`}</FormLabel>
              <Select value={field.value || NO_SELECTION} onValueChange={(value) => field.onChange(value === NO_SELECTION ? '' : value)}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {!isCorrection && <SelectItem value={NO_SELECTION}>Nicht vorbefüllen</SelectItem>}
                  {countries.map((country) => (
                    <SelectItem key={country.value} value={country.value}>
                      {country.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <CustomerTextField control={control} name="invoiceMail" label="Rechnungs-E-Mail" type="email" placeholder="E-Mail für Rechnungen" />
      </div>
    </OfferFormSection>
  );
};

interface CustomerTextFieldProps {
  control: Control<OfferFormValues>;
  name: Exclude<OfferCustomerFieldName, 'country' | 'newCustomerDate' | 'iban' | 'bic'>;
  label: string;
  type?: 'text' | 'email' | 'tel';
  placeholder?: string;
  hint?: string;
  className?: string;
  disabled?: boolean;
  /** Live check result – shown as icon in the field, `takenMessage` below it when the value is taken. */
  availability?: AvailabilityCheck;
  takenMessage?: string;
}

const CustomerTextField = ({
  control,
  name,
  label,
  type = 'text',
  placeholder,
  hint,
  className,
  disabled,
  availability,
  takenMessage
}: CustomerTextFieldProps) => (
  <FormField
    control={control}
    name={`customer.${name}`}
    render={({ field, fieldState }) => (
      <FormItem className={cn('flex flex-col gap-[10px]', className)}>
        <FormLabel className="text-[14px] font-bold text-[#12282A]">{label}</FormLabel>
        <div className="relative">
          <FormControl>
            <Input {...field} type={type} placeholder={placeholder} disabled={disabled} className={cn(availability && 'pr-9')} />
          </FormControl>
          {availability && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              <CheckStatusIcon isChecking={availability.isChecking} isValid={availability.isAvailable} />
            </span>
          )}
        </div>
        {hint && <p className="text-[12px] text-[#888]">{hint}</p>}
        <FormMessage />
        {/* A conflict from saving is already the field error – the live hint would repeat it. */}
        {availability?.isAvailable === false && !fieldState.error && takenMessage && (
          <p className="text-[0.8rem] font-medium text-destructive">{takenMessage}</p>
        )}
      </FormItem>
    )}
  />
);

interface CheckedCustomerTextFieldProps extends Omit<CustomerTextFieldProps, 'availability'> {
  /** The value as the backend compares it, or `''` while it is not checkable. */
  toCheckable: (value: string) => string;
  check?: AvailabilityCheckFn;
}

/** A customer field with the live check – watches only its own value, so typing does not re-render the whole section. */
const CheckedCustomerTextField = ({ toCheckable, check, ...props }: CheckedCustomerTextFieldProps) => {
  const value = useWatch({ control: props.control, name: `customer.${props.name}` });
  const availability = useAvailabilityCheck(toCheckable(value ?? ''), check);
  return <CustomerTextField {...props} availability={check ? availability : undefined} />;
};

export default OfferCustomerSection;
